import getConfig from 'next/config';
import axios, { AxiosRequestConfig, AxiosRequestHeaders } from 'axios';
import { NextPageContext } from 'next';
import applyCaseMiddleware from 'axios-case-converter';
import { IncomingHttpHeaders } from 'http';

type AxiosApiParams = AxiosRequestConfig;

const { publicRuntimeConfig } = getConfig();
const API_URL = publicRuntimeConfig.UCHI_URL;
const INTERNAL_API_URL = publicRuntimeConfig.INTERNAL_API_URL;
const hasUidSet = !!publicRuntimeConfig.STUDENT_UID;

const UID_COOKIE = 'uid';
const USER_TYPE_HEADER = 'uchi-user-type';
const USER_ID_HEADER = 'uchi-user-id';
const USER_ACCOUNT_ID_HEADER = 'uchi-account-id';

const axiosDefaults = {
  withCredentials: true,
};

axios.defaults = { ...axios.defaults, ...axiosDefaults };

const isServer = typeof window === 'undefined';
const isProduction = process.env.NODE_ENV === 'production';

const serverSidePath = isProduction && !!INTERNAL_API_URL ? INTERNAL_API_URL : API_URL;

const clientProxyPath = typeof window !== 'undefined' && `${window.location.origin}`;

const addBasePath = (url?: string) => {
  const clientSidePath = isProduction ? API_URL : clientProxyPath;

  const basePath = isServer ? serverSidePath : clientSidePath;

  return `${basePath}${url}`;
};

export const getAuthHeaders = (headers: IncomingHttpHeaders) => ({
  [USER_ID_HEADER]: headers[USER_ID_HEADER] as string,
  [USER_TYPE_HEADER]: headers[USER_TYPE_HEADER] as string,
  [USER_ACCOUNT_ID_HEADER]: headers[USER_ACCOUNT_ID_HEADER] as string,
});

export async function axiosAPI(params: AxiosApiParams, context?: NextPageContext) {
  const url = addBasePath(params.url);

  const client = applyCaseMiddleware(axios.create());

  let config: AxiosRequestConfig;

  if (isProduction && context?.req?.headers) {
    const headers = {
      ...getAuthHeaders(context.req.headers),
      ...params.headers,
    };

    config = {
      ...params,
      url,
      headers: {
        ...headers,
      } as unknown as AxiosRequestHeaders,
    };
  } else if (isProduction) {
    config = {
      ...params,
      url,
      headers: {
        ...params.headers,
      } as unknown as AxiosRequestHeaders,
    };
  } else {
    config = {
      ...params,
      url,
      ...(hasUidSet && {
        headers: {
          ...params.headers,
          cookie: `${UID_COOKIE}=${publicRuntimeConfig.STUDENT_UID};`,
        },
      }),
    };
  }

  const { data } = await client(config);

  return data;
}

export const tryGetUserFromHeaders = (headers: IncomingHttpHeaders) => {
  const uid = headers[USER_ID_HEADER];
  const accountId = headers[USER_ACCOUNT_ID_HEADER];
  const type = headers[USER_TYPE_HEADER];

  return !!(
    !!uid &&
    typeof uid === 'string' &&
    !!type &&
    typeof type === 'string' &&
    accountId &&
    typeof accountId === 'string'
  );
};
